/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { ReactNode, useState } from "react";
import { userAccountService } from "../services";
import {
  IAuthParams,
  ICompletedApplication,
  ICreateApplicationRequest,
  ILoginApplicationRequest,
} from "../services/user-account-service.types";
import { FormStep, IFormModel, IUpdateFormValues } from "../formModels";
import { INITIAL_FORM_VALUES } from "../formModels/initialFormValues";
import { useUser } from "./UserContext";
import mapOnlineApplication from "../helpers/map-online-application";
import { IOnlineApplication } from "../services/online-application.types";
import { getApplicationStep } from "../helpers/map-online-application-sections";
import { JOINING_APP_ROUTES } from "../config/common";
import { CD_USER_TYPE } from "../constants/PerformerType";
import { IAboutYouFormModel } from "../formModels/AboutYouFormModel";

export type ResumeApplication = (
  securityKey: string,
  onlineApplicationId: string,
  step?: string
) => Promise<{ success: boolean; performerType?: number | undefined }>;

interface ApplicationContextParams {
  onlineApplicationId?: number | string;
  securityKey?: string;
  recurlyAccountId?: string;
  setRecurlyAccountId: (recurlyAccountId: string) => void;
  getAuthParams: () => IAuthParams;
  createApplication: (
    createApplicationParams: ICreateApplicationRequest
  ) => Promise<boolean>;
  loginToApplication: (
    request: ILoginApplicationRequest
  ) => Promise<{ success: boolean }>;
  applicationData: IFormModel;
  stepNumber: number;
  eligibilitySubStep: number;
  saveApplicationData: (updateFormValues?: IUpdateFormValues) => void;
  goToStep: (
    step: "prev" | "next" | number,
    updateFormValues?: IUpdateFormValues
  ) => void;
  goToNextStep: () => void;
  goToPrevStep: () => void;
  gotToNextSubStep: () => void;
  gotToPrevSubStep: () => void;
  clearApplicationData: () => void;
  resumeApplication: ResumeApplication;
  setOnlineApplicationId: (appId: number | string) => void;
  setSecurityKey: (securityKey?: string) => void;
  setStepNumber: (step: number) => void;
  setApplicationDetails: (appDetail: IOnlineApplication) => void;
  setApplicationData: (formData: IFormModel) => void;
}

export const ApplicationContext = React.createContext<ApplicationContextParams>(
  {
    getAuthParams: () => ({} as IAuthParams),
    onlineApplicationId: undefined,
    recurlyAccountId: undefined,
    securityKey: undefined,
    applicationData: INITIAL_FORM_VALUES,
    stepNumber: 0,
    eligibilitySubStep: 0,
    setRecurlyAccountId: () => {},
    saveApplicationData: () => {},
    goToStep: () => {},
    goToNextStep: () => {},
    goToPrevStep: () => {},
    gotToNextSubStep: () => {},
    gotToPrevSubStep: () => {},
    createApplication: () => new Promise(() => {}),
    loginToApplication: () => new Promise(() => {}),
    clearApplicationData: () => {},
    resumeApplication: () => new Promise(() => {}),
    setOnlineApplicationId: () => undefined,
    setSecurityKey: () => undefined,
    setStepNumber: () => undefined,
    setApplicationDetails: () => undefined,
    setApplicationData: () => undefined,
  }
);

interface ApplicationContextProviderProps {
  children: ReactNode;
}

export const ApplicationContextProvider = ({
  children,
}: ApplicationContextProviderProps) => {
  const [onlineApplicationId, setOnlineApplicationId] = useState<
    number | string
  >();
  const [securityKey, setSecurityKey] = useState<string>();
  const [recurlyAccountId, setRecurlyAccountId] = useState<string>();
  const [stepNumber, setStepNumber] = useState(0);
  const [eligibilitySubStep, setEligibilitySubStep] = useState(0);
  const [applicationData, setApplicationData] =
    useState<IFormModel>(INITIAL_FORM_VALUES);
  // isCDApplication and CD_USER_TYPE required for old cd joining,
  // can be removed after clean FEATURE_FLAGS.JoiningCDProcess
  const { updateUser, clearUser, isCDApplication } = useUser();

  const getAuthParams = (): IAuthParams => {
    const authParams = {
      onlineApplicationId: String(onlineApplicationId),
      securityKey,
    };

    // isCDApplication and CD_USER_TYPE required for old cd joining,
    // can be removed after clean FEATURE_FLAGS.JoiningCDProcess
    return isCDApplication
      ? { isCDApplication, userType: CD_USER_TYPE, ...authParams }
      : authParams;
  };

  const saveApplicationData = (updateFormValues?: IUpdateFormValues) => {
    if (updateFormValues && updateFormValues.formValues) {
      const aboutYouInformationForm = (
        updateFormValues?.formValues as IAboutYouFormModel
      )?.aboutYouInformationForm;

      setApplicationData((existingFormData) => ({
        ...existingFormData,
        [updateFormValues.formStep]: updateFormValues.formValues,
        // for MP Current Member Block has its own Form
        ...(aboutYouInformationForm && {
          [FormStep.AboutYouMPCurrentMember]: {
            currentEmail: aboutYouInformationForm.currentEmail,
            currentMember: aboutYouInformationForm.currentMember,
            currentName: aboutYouInformationForm.currentName,
            currentPerformerType: aboutYouInformationForm.currentPerformerType,
          },
        }),
      }));
    }
  };

  const clearApplicationData = () => {
    setApplicationData(INITIAL_FORM_VALUES);
    setOnlineApplicationId(undefined);
    setSecurityKey(undefined);
    setStepNumber(0);
    setEligibilitySubStep(0);
    clearUser();
  };

  const goToStep = (
    step: "prev" | "next" | number,
    updateFormValues?: IUpdateFormValues
  ) => {
    saveApplicationData(updateFormValues);

    if (step === "prev") {
      return setStepNumber((currentStep) => currentStep - 1);
    }

    if (step === "next") {
      return setStepNumber((currentStep) => currentStep + 1);
    }

    return setStepNumber(step);
  };

  const goToNextStep = (formValues?: IUpdateFormValues) =>
    goToStep("next", formValues);
  const goToPrevStep = (formValues?: IUpdateFormValues) =>
    goToStep("prev", formValues);

  const gotToNextSubStep = () => {
    setEligibilitySubStep((currentStep) => currentStep + 1);
  };

  const gotToPrevSubStep = () => {
    setEligibilitySubStep((currentStep) => currentStep - 1);
  };

  const createApplication = async (request: ICreateApplicationRequest) => {
    try {
      const applicationDataResponse =
        await userAccountService.createApplication(request);

      setApplicationDetails(applicationDataResponse);

      // new application start with blank app
      setApplicationData(INITIAL_FORM_VALUES);

      return true;
    } catch (error: any) {
      return error.message || false;
    }
  };

  const loginToApplication = async (request: ILoginApplicationRequest) => {
    try {
      const applicationDataResponse =
        (await userAccountService.loginToApplication(
          request
        )) as IOnlineApplication;

      if (isAppCompleted(applicationDataResponse)) {
        return redirectToCompletedPage(applicationDataResponse);
      }

      if (isLegacyApp(applicationDataResponse)) {
        return redirectToLegacyApp();
      }

      setApplicationDetails(applicationDataResponse);

      // maps returned application to form model
      const mappedApplicationData = mapOnlineApplication(
        applicationDataResponse
      );
      setApplicationData(mappedApplicationData);

      // set the page number here based on the response also
      const { mainStep, subStep } = getApplicationStep(applicationDataResponse);
      setStepNumber(mainStep);
      setEligibilitySubStep(subStep);
      setRecurlyAccountId(applicationDataResponse?.accountId);

      return { success: true };
    } catch (error: any) {
      console.error(error);

      return { success: false };
    }
  };

  const resumeApplication = async (
    securityKey: string,
    onlineApplicationId: string
  ) => {
    try {
      const applicationDataResponse = (await userAccountService.getApplication({
        securityKey,
        onlineApplicationId,
      })) as IOnlineApplication;

      const { performerType } = applicationDataResponse;

      if (isAppCompleted(applicationDataResponse)) {
        return redirectToCompletedPage(applicationDataResponse);
      }

      if (isLegacyApp(applicationDataResponse)) {
        return redirectToLegacyApp();
      }

      setApplicationDetails(applicationDataResponse);

      // maps returned application to form model
      const mappedApplicationData = mapOnlineApplication(
        applicationDataResponse
      );
      setApplicationData(mappedApplicationData);

      const { mainStep, subStep } = getApplicationStep(applicationDataResponse);

      // set the page number here based on the response also
      setStepNumber(mainStep);
      setEligibilitySubStep(subStep);
      setRecurlyAccountId(applicationDataResponse?.accountId);

      return { success: true, performerType };
    } catch (error: any) {
      console.error(error);

      return { success: false };
    }
  };

  const setApplicationDetails = (onlineApplication: IOnlineApplication) => {
    const { id, securityKey, ...rest } = onlineApplication;

    updateUser((userDetails) => ({
      ...userDetails,
      ...rest,
    }));

    setSecurityKey(securityKey);
    setOnlineApplicationId(id);
  };

  return (
    <ApplicationContext.Provider
      value={{
        getAuthParams,
        onlineApplicationId,
        securityKey,
        applicationData,
        stepNumber,
        eligibilitySubStep,
        recurlyAccountId,
        setRecurlyAccountId,
        saveApplicationData,
        goToStep,
        goToNextStep,
        goToPrevStep,
        gotToNextSubStep,
        gotToPrevSubStep,
        createApplication,
        setApplicationData,
        loginToApplication,
        clearApplicationData,
        resumeApplication,
        setOnlineApplicationId,
        setSecurityKey,
        setStepNumber,
        setApplicationDetails,
      }}
    >
      {children}
    </ApplicationContext.Provider>
  );
};

export const useApplication = () => React.useContext(ApplicationContext);

const isAppCompleted = (
  response: IOnlineApplication | ICompletedApplication
) => {
  // @ts-expect-error - response is of type IOnlineApplication | ICompletedApplication
  if (response?.completedDate) {
    return true;
  }

  return false;
};

export const redirectToCompletedPage = (applicationDataResponse: any) => {
  const { completedDate, forenames, surname } =
    applicationDataResponse as ICompletedApplication;
  window.location.replace(
    `${JOINING_APP_ROUTES.BASE}${JOINING_APP_ROUTES.PERFORMER.PREVIOUSLY_COMPLETE}?completedDate=${completedDate}&forenames=${forenames}&surname=${surname}`
  );

  return { success: true };
};

export const isLegacyApp = (applicationDataResponse: any) => {
  const { modernJourney } = applicationDataResponse;

  return !modernJourney;
};

const redirectToLegacyApp = () => {
  window.location.replace("https://www.spotlight.com/join/#continue");

  return { success: true };
};
